package com.sddamico.swigmore.ds.core

import androidx.compose.runtime.Composable
import com.sddamico.swigmore.ds.SwColor
import com.sddamico.swigmore.ds.SwEffects
import com.sddamico.swigmore.ds.css
import com.sddamico.swigmore.ds.stitch
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.dom.Div

/**
 * - [Ticket]: cream stock with the dashed stitch — rankings, ballot card, sign-in, week cards.
 * - [Field]: darker green inset panel with a hairline — matchups, the field, admin side panels.
 * - [Plain]: cream without the stitch — stat tiles, the submissions table.
 * - [Gold]: gold confirmation band ("Your ballot is in").
 * - [Paper]: off-white sheet for the email preview.
 */
enum class CardVariant { Ticket, Field, Plain, Gold, Paper }

@Composable
fun Card(
  modifier: Modifier = Modifier,
  variant: CardVariant = CardVariant.Ticket,
  padding: String = "16px",
  radius: String = SwEffects.radiusLg,
  stitchInset: String = "6px",
  onClick: (() -> Unit)? = null,
  content: @Composable () -> Unit,
) {
  val handler = onClick
  Div(
    attrs = Modifier
      .css("border-radius" to radius, "padding" to padding, "box-sizing" to "border-box")
      .cardSurface(variant, stitchInset)
      .then(if (onClick != null) Modifier.css("cursor" to "pointer") else Modifier)
      .then(modifier)
      .toAttrs {
        if (handler != null) {
          attr("role", "button")
          attr("tabindex", "0")
          onClick { handler() }
        }
      }
  ) { content() }
}

internal fun Modifier.cardSurface(variant: CardVariant, stitchInset: String = "6px"): Modifier = when (variant) {
  CardVariant.Ticket -> css("background" to SwColor.cream, "color" to SwColor.ink).stitch(SwColor.stitch, stitchInset)
  CardVariant.Plain -> css("background" to SwColor.cream, "color" to SwColor.ink)
  CardVariant.Field -> css(
    "background" to SwColor.field800,
    "color" to SwColor.cream,
    "border" to "1px solid ${SwColor.hairline}",
  )
  CardVariant.Gold -> css("background" to SwColor.gold, "color" to SwColor.ink)
  CardVariant.Paper -> css("background" to SwColor.paper, "color" to SwColor.ink, "overflow" to "hidden")
}
